import React, { useRef, useEffect } from 'react'
import { Maximize2, Minimize2 } from 'lucide-react'

interface ExpandableCardProps {
  title: string
  children: React.ReactNode
  isExpanded: boolean
  onExpand: () => void
  onCollapse: () => void
}

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  title,
  children,
  isExpanded,
  onExpand,
  onCollapse,
}) => {
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isExpanded) return

    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        onCollapse()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isExpanded, onCollapse])

  const handleCardClick = () => {
    if (!isExpanded) {
      onExpand()
    }
  }

  return (
    <div className="w-full">
      <div
        ref={cardRef}
        onClick={handleCardClick}
        className={`
          rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200
          ${isExpanded ? 'relative z-[1]' : 'cursor-pointer'}
        `}
      >
        {/* Header with title and expand/collapse button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="font-medium text-gray-900">{title}</span>
          <button
            onClick={(event) => {
              event.stopPropagation()
              if (isExpanded) {
                onCollapse()
              } else {
                onExpand()
              }
            }}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label={isExpanded ? 'Minimize' : 'Expand'}
          >
            {isExpanded ? (
              <Minimize2 size={18} strokeWidth={2} />
            ) : (
              <Maximize2 size={18} strokeWidth={2} />
            )}
          </button>
        </div>

        {/* Content area */}
        <div
          className={`
            overflow-hidden transition-all duration-200
            ${isExpanded ? 'overflow-y-auto' : ''}
          `}
          style={
            isExpanded
              ? {
                  aspectRatio: '16 / 9',
                  maxHeight: 'calc(100vh - 280px)',
                }
              : undefined
          }
        >
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  )
}
